import { Typography, Icon, tk } from '@sunwebgroup/ui-components';
import type { CMSFacilityType } from '@/cms/fields/mappers/productDetailPageFieldsMapper';
import { styled } from 'styled-components';
import { useIntl } from 'react-intl';

interface FacilityTypeProps {
  facilityType: CMSFacilityType;
  maxItems?: number;
}

const Container = styled.div`
  &:not(:last-child) {
    border-bottom: 2px solid ${tk('color_semantic_border')};
    margin-bottom: ${tk('spacing_lg')};
  }
`;

const FacilityTypeTitle = styled.div`
  display: flex;
  gap: ${tk('spacing_xs')};
  align-items: center;
  margin-bottom: ${tk('spacing_md')};
`;

const FacilityTypeContent = styled.ul`
  column-gap: ${tk('spacing_2xl')};

  @media ${tk('mq_tablet')} {
    column-count: 2;
  }

  @media ${tk('mq_desktop_sm')} {
    column-count: 3;
  }
`;

const FacilityItem = styled(Typography)`
  margin-bottom: ${tk('spacing_xs')};
`;

export default function FacilityType({ facilityType, maxItems }: FacilityTypeProps) {
  const intl = useIntl();
  const facilityTypeNumberOfItems = facilityType?.items?.length ?? 0;
  const numberOfItemsToShow = Math.min(maxItems ?? facilityTypeNumberOfItems, facilityTypeNumberOfItems);

  if (!facilityType) {
    return null;
  }

  return (
    <Container>
      <FacilityTypeTitle>
        {facilityType.icon && <Icon size="md" glyph={facilityType.icon} />}
        <Typography $variant="body-lg-bold" as="h3">
          {intl.formatMessage({ id: facilityType.title })}
        </Typography>
      </FacilityTypeTitle>
      <FacilityTypeContent>
        {facilityType.items.slice(0, numberOfItemsToShow).map((facility) => (
          <FacilityItem $variant="body-md" as="li" key={facility}>
            {facility}
          </FacilityItem>
        ))}
      </FacilityTypeContent>
    </Container>
  );
}
